// CB:BEGIN project-timeline-component
import React, { useEffect, useState } from 'react';

interface TimelineEvent {
  id: string;
  eventType: string;
  actorRole?: string;
  occurredAt: string;
  metadata?: Record<string, unknown>;
}

interface ProjectTimelineProps {
  projectId: string;
  defaultCollapsed?: boolean;
  className?: string;
}

const EVENT_LABELS: Record<string, string> = {
  PROJECT_CREATED: 'Project created',
  PROJECT_SUBMITTED: 'Project submitted',
  REPORT_ATTACHED: 'Measurement report attached',
  BID_SUBMITTED: 'Bid submitted',
  BID_ACCEPTED: 'Contractor selected',
  CONTRACT_SIGNED_HOMEOWNER: 'Homeowner signed contract',
  CONTRACT_SIGNED_CONTRACTOR: 'Contractor signed contract',
  CONTRACT_FULLY_SIGNED: 'Contract fully signed',
  MANUAL_CONTRACT_UPLOADED: 'Signed contract uploaded (manual)',
  ESCROW_CREATED: 'Escrow account created',
  ESCROW_FUNDED: 'Escrow funded',
  DEPOSIT_CAPTURED: 'Deposit captured',
  PROJECT_STARTED: 'Project started',
  SCHEDULED_START_SET: 'Start date set',
  MILESTONE_EVIDENCE_SUBMITTED: 'Milestone evidence submitted',
  MILESTONE_EVIDENCE_APPROVED: 'Milestone evidence approved',
  MILESTONE_RELEASED: 'Milestone payment released',
  PROJECT_AUTO_COMPLETED: 'Project completed',
  DISPUTE_OPENED: 'Dispute opened',
  DISPUTE_RESOLVED: 'Dispute resolved',
  ESCROW_FROZEN: 'Escrow frozen (dispute)',
  ESCROW_UNFROZEN: 'Escrow unfrozen',
  ADMIN_NOTE_ADDED: 'Admin note added',
  OVERRIDE_TRANSITION: 'Admin override applied',
};

function formatEventType(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ProjectTimeline({ projectId, defaultCollapsed = false, className = '' }: ProjectTimelineProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    if (loaded) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/projects/${projectId}/timeline`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events ?? []);
      }
    } catch {
      // silent — timeline is non-critical
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (!collapsed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, collapsed]);

  return (
    <div className={`rounded-lg border border-gray-200 bg-white ${className}`}>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex justify-between items-center px-4 py-3 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-lg"
      >
        <span>Project Timeline</span>
        <span className="text-gray-400 text-xs">{collapsed ? '▼ Show' : '▲ Hide'}</span>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4">
          {loading ? (
            <p className="text-sm text-gray-400">Loading timeline…</p>
          ) : events.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No events recorded yet.</p>
          ) : (
            <ol className="relative border-l border-gray-200 ml-2 space-y-4 mt-2">
              {events.map((event) => (
                <li key={event.id} className="ml-4">
                  <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-blue-400 border-2 border-white" />
                  <div className="flex justify-between items-start gap-2">
                    <p className="text-sm text-gray-800 font-medium">{formatEventType(event.eventType)}</p>
                    {event.actorRole && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded flex-shrink-0">{event.actorRole}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(event.occurredAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectTimeline;
// CB:END project-timeline-component
